import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';

import LoadingButton from '@mui/lab/LoadingButton';
import { TeamType } from '../../models';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTeamManagement } from './useTeamManagement';
import { z } from "zod";

interface InviteUsersDialogProps {
    open: boolean;
    onClose: () => void;
}

export const InviteUsersDialog = ({
    open,
    onClose
}: InviteUsersDialogProps) => {
    const [emails, setEmails] = useState('');
    const [role, setRole] = useState<TeamType>(TeamType.MEMBER);
    const [error, setError] = useState<string | null>(null);
    const snackbar = useSnackbar();
    const {
        inviteUserToOrganization,
        ownerTeamMembers,
        memberTeamMembers,
        invitations,
        getUserById
    } = useTeamManagement();
    const isLoading = inviteUserToOrganization.isLoading;

    const emailSchema = z.string().email();
    const emailListSchema = z.string().transform((str: string) =>
        str.split(',')
            .map((email: string) => email.trim())
            .filter((email: string) => email.length > 0)
    ).pipe(z.array(emailSchema));

    const validateEmails = (emailList: string): string[] => {
        try {
            return emailListSchema.parse(emailList);
        } catch (error) {
            return [];
        }
    };

    const handleSubmit = async () => {
        setError(null);
        const validEmails = validateEmails(emails);

        if (validEmails.length === 0) {
            snackbar.enqueueSnackbar('Please enter at least one valid email address', { variant: 'error' });
            return;
        }

        // Get all current team members' emails
        const allTeamMembers = [...(ownerTeamMembers ?? []), ...(memberTeamMembers ?? [])];
        const existingMemberEmails = allTeamMembers
            .map(member => getUserById(member.userId)?.email)
            .filter((email): email is string => !!email);

        // Get all pending invitation emails
        const pendingInvitationEmails = invitations?.map(inv => inv.email) ?? [];

        // Find emails that are already members or have pending invitations
        const alreadyMembers = validEmails.filter(email => existingMemberEmails.includes(email));
        const pendingInvites = validEmails.filter(email => pendingInvitationEmails.includes(email));
        const validNewEmails = validEmails.filter(
            email => !existingMemberEmails.includes(email) && !pendingInvitationEmails.includes(email)
        );

        if (validNewEmails.length === 0) {
            let errorMessage = '';
            if (alreadyMembers.length > 0) {
                errorMessage += `The following users are already team members: ${alreadyMembers.join(', ')}. `;
            }
            if (pendingInvites.length > 0) {
                errorMessage += `The following users already have pending invitations: ${pendingInvites.join(', ')}.`;
            }
            setError(errorMessage);
            return;
        }

        await inviteUserToOrganization.mutateAsync({
            emails: validNewEmails,
            targetRole: role
        });

        // Show success message with any skipped emails
        let successMessage = `Successfully invited ${validNewEmails.length} user${validNewEmails.length > 1 ? 's' : ''}.`;
        if (alreadyMembers.length > 0 || pendingInvites.length > 0) {
            successMessage += ' Some emails were skipped as they are already members or have pending invitations.';
        }
        snackbar.enqueueSnackbar(successMessage, { variant: 'success' });

        onClose();
        setEmails('');
        setRole(TeamType.MEMBER);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Invite Team Members</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <TextField
                    select
                    margin="dense"
                    label="Role"
                    fullWidth
                    value={role}
                    onChange={(e) => setRole(e.target.value as TeamType)}
                    sx={{ mt: 2 }}
                >
                    <MenuItem value={TeamType.OWNER}>Owner</MenuItem>
                    <MenuItem value={TeamType.MEMBER}>Member</MenuItem>
                </TextField>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Email Addresses"
                    type="text"
                    fullWidth
                    value={emails}
                    onChange={(e) => {
                        setEmails(e.target.value);
                        setError(null);
                    }}
                    placeholder="Example: user1@example.com, user2@example.com"
                    multiline
                    rows={3}
                />
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Example: user1@example.com, user2@example.com
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={isLoading}
                    variant='outlined'
                    color='error'
                >
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isLoading}
                    loading={isLoading}
                >
                    Send Invitations
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}; 